// client/src/components/dashboard/properties/SharePropertyModal.js
import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { useTranslation } from 'react-i18next';
import { FaTrashAlt } from 'react-icons/fa'; // For delete button
import LoadingSpinner from '../../common/LoadingSpinner';
// Import REAL API functions
import {
    searchUsers,
    fetchSharesForProperty,
    shareProperty,
    deleteShare
} from '../../../api/authApi'; // Adjust path if needed

import './SharePropertyModal.css'; // Make sure CSS file exists

// Define available permissions (adjust keys/labels as needed)
const availablePermissions = [
    { value: 'view', labelKey: 'share.perm.view', defaultLabel: 'View Devices' },
    { value: 'control', labelKey: 'share.perm.control', defaultLabel: 'Control Devices' },
    { value: 'manage', labelKey: 'share.perm.manage', defaultLabel: 'Manage Property/Shares' },
    // Add more specific permissions if backend supports them
];

function SharePropertyModal({ property, onClose }) {
    const { t } = useTranslation();
    const [currentShares, setCurrentShares] = useState([]); // List of users property is shared with
    const [loadingList, setLoadingList] = useState(true); // Loading state for the list
    const [listError, setListError] = useState(''); // Error fetching list

    const [searchTerm, setSearchTerm] = useState('');
    const [foundUsers, setFoundUsers] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');

    const [selectedUser, setSelectedUser] = useState(null); // User selected from search
    const [permissions, setPermissions] = useState([]); // Permissions for the NEW share
    const [addShareError, setAddShareError] = useState('');
    const [addShareLoading, setAddShareLoading] = useState(false);

    const [actionLoading, setActionLoading] = useState({ delete: null }); // For deleting existing shares

    // Function to fetch existing shares for this property
    const loadShares = useCallback(async () => {
        if (!property?.property_id) return; // Need property ID
        setLoadingList(true);
        setListError('');
        try {
            console.log(`[ShareModal] Fetching shares for property ID: ${property.property_id}`);
            const shares = await fetchSharesForProperty(property.property_id);
            setCurrentShares(shares || []);
            console.log("[ShareModal] Shares received:", shares);
        } catch (err) {
            console.error("Error loading shares:", err);
            setListError(err.message || t('errors.api.requestFailed'));
        } finally {
            setLoadingList(false);
        }
    }, [property?.property_id, t]);

    // Fetch shares when the modal opens (property prop changes)
    useEffect(() => {
        loadShares();
    }, [loadShares]);

    // --- Handlers for Add New Share ---

    // Handler for user search input (debounced search might be better)
    const handleSearchChange = async (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setSelectedUser(null); // Clear selection if search term changes
        setFoundUsers([]); // Clear previous results
        setSearchError('');
        setAddShareError(''); // Clear add error

        if (term.length > 1) { // Start searching after 1 or 2 chars
            setSearchLoading(true);
            try {
                const users = await searchUsers(term); // Use REAL API call
                setFoundUsers(users || []);
                if (users.length === 0) {
                    setSearchError(t('share.errors.noUsersFound', 'No matching users found.'));
                }
            } catch (err) {
                console.error("Error searching users:", err);
                setSearchError(err.message || t('share.errors.searchFailed', 'Failed to search users.'));
            } finally {
                setSearchLoading(false);
            }
        }
    };

    // Handler for selecting a user from search results
    const handleUserSelect = (user) => {
        // Check if already shared with this user
        if (currentShares.some(share => share.shared_with_user_id === user.uid)) {
             setSearchError(t('share.errors.alreadyShared', 'Property already shared with this user.'));
             setFoundUsers([]);
             setSearchTerm('');
             return;
        }
        setSelectedUser(user);
        setSearchTerm(user.username); // Fill input with selected username
        setFoundUsers([]); // Hide search results list
        setSearchError(''); // Clear search error
        setPermissions([]); // Reset permissions for new selection
    };

    // Handler for permission checkboxes
    const handlePermissionChange = (event) => {
        const { value, checked } = event.target;
        setPermissions(prev =>
            checked ? [...prev, value] : prev.filter(p => p !== value)
        );
        setAddShareError(''); // Clear error when changing permissions
    };

    // Handler for submitting the NEW share action
    const handleShareSubmit = async () => {
        setAddShareError('');
        if (!selectedUser) {
            setAddShareError(t('share.errors.noUserSelected', 'Please search and select a user to share with.'));
            return;
        }
        if (permissions.length === 0) {
             setAddShareError(t('share.errors.noPermissions', 'Please select at least one permission to grant.'));
             return;
        }

        setAddShareLoading(true);
        try {
            console.log(`Sharing Property ${property.property_id} with User ${selectedUser.uid} permissions: ${permissions.join(', ')}`);
            // Use REAL API call
            await shareProperty(property.property_id, selectedUser.uid, permissions);
            // alert(t('share.success', 'Property shared successfully!')); // Optional: Use message state instead?
            setSelectedUser(null); // Clear selection
            setSearchTerm('');     // Clear search term
            setPermissions([]);    // Clear permissions
            loadShares();          // Refresh the list of current shares

        } catch (err) {
            console.error("Error sharing property:", err);
            setAddShareError(err.message || t('errors.api.requestFailed'));
        } finally {
            setAddShareLoading(false);
        }
    };

     // --- Handler for Deleting an Existing Share ---
     const handleDeleteShare = async (shareId, username) => {
         if (!shareId || actionLoading.delete === shareId) return;
         // Added username to confirmation
        //   if (window.confirm(t('share.confirmDelete', `Are you sure you want to stop sharing this property with ${username}?`))) {
              setActionLoading(prev => ({ ...prev, delete: shareId }));
              setListError(''); // Clear list error
              try {
                  console.log(`Attempting to delete share ID: ${shareId}`);
                  await deleteShare(shareId); // Use REAL API function
                  loadShares(); // Refresh the list
              } catch (err) {
                  console.error("Error deleting share:", err);
                  setListError(err.message || t('errors.api.requestFailed'));
              } finally {
                   setActionLoading(prev => ({ ...prev, delete: null }));
            //   }
          }
     };


    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content share-modal" onClick={e => e.stopPropagation()}>
                <h2>{t('share.title', 'Share Property')}: {property?.property_name}</h2>

                 {/* Display Existing Shares */}
                 <h4>{t('share.currentlySharedWith', 'Currently Shared With:')}</h4>
                 {loadingList && <LoadingSpinner size="small" />}
                 {listError && <div className="form-error">{listError}</div>}
                 {!loadingList && currentShares.length > 0 && (
                     <ul className="current-shares-list">
                         {currentShares.map(share => (
                             <li key={share.share_id}>
                                 <span className="shared-user-info">
                                     {share.shared_with_username || `User ID ${share.shared_with_user_id}`}
                                     <span className="shared-permissions">
                                         ({share.permissions?.join(', ') || t('share.noPermissions', 'No Permissions')})
                                     </span>
                                 </span>
                                 {/* Delete Button for existing share */}
                                 <button
                                     onClick={() => handleDeleteShare(share.share_id, share.shared_with_username)}
                                     className="delete-button small-action-button" // Use specific class
                                     title={t('share.removeAccess', 'Remove Access')}
                                     disabled={actionLoading.delete === share.share_id}
                                 >
                                     {actionLoading.delete === share.share_id ? <LoadingSpinner size="extrasmall"/> : <FaTrashAlt />}
                                 </button>
                             </li>
                         ))}
                     </ul>
                 )}
                  {!loadingList && currentShares.length === 0 && (
                      <p className="no-shares-message">{t('share.notShared', 'Not currently shared with anyone.')}</p>
                  )}

                 <hr />

                 {/* Add New Share Section */}
                 <h4>{t('share.shareWithNew', 'Share with New User')}</h4>

                 {/* User Search Input */}
                 <div className="form-group search-user-group">
                     <label htmlFor="share-search">{t('share.searchUser', 'Search User (Username/Email)')}:</label>
                     <input
                         type="text" id="share-search" value={searchTerm}
                         onChange={handleSearchChange}
                         placeholder={t('share.searchPlaceholder', 'Enter username or email...')}
                         disabled={addShareLoading}
                     />
                     {searchLoading && <LoadingSpinner size="extrasmall" />}
                     {/* Search Results Dropdown */}
                     {foundUsers.length > 0 && (
                         <ul className="search-results-list">
                             {foundUsers.map(user => (
                                 <li key={user.uid} onClick={() => handleUserSelect(user)}>
                                     {user.username} {user.email ? `(${user.email})` : ''}
                                 </li>
                             ))}
                         </ul>
                     )}
                     {/* Display selected user or search error */}
                     {searchError && <div className="form-error">{searchError}</div>}
                     {!searchError && selectedUser && <div className="info-message">{`${t('share.selectedUser', 'Selected')}: ${selectedUser.username}`}</div>}
                </div>


                {/* Permissions Selection */}
                {selectedUser && ( // Only show if a user is selected
                    <div className="form-group permissions-group">
                         <label>{t('share.permissions', 'Permissions')}:</label>
                         <div className="checkbox-group">
                            {availablePermissions.map(perm => (
                                <div key={perm.value}>
                                    <input type="checkbox" id={`perm-${perm.value}`} value={perm.value} onChange={handlePermissionChange} checked={permissions.includes(perm.value)} />
                                    <label htmlFor={`perm-${perm.value}`}>{t(perm.labelKey, perm.defaultLabel)}</label>
                                </div>
                            ))}
                         </div>
                    </div>
                )}

                {/* Display Add Share Error */}
                {addShareError && <div className="form-error">{addShareError}</div>}

                {/* Modal Actions */}
                <div className="modal-actions">
                     <button onClick={handleShareSubmit} className="form-action-button save-button" disabled={addShareLoading || !selectedUser || permissions.length === 0}>
                        {addShareLoading ? t('common.sharing', 'Sharing...') : t('common.share', 'Share')}
                     </button>
                     <button onClick={onClose} type="button" className="form-action-button cancel-button" disabled={addShareLoading}>
                        {t('common.close', 'Close')}
                     </button>
                </div>
            </div>
        </div>
    );
}

export default SharePropertyModal;